/**
 * Pipelines and their stages: rename, reorder, add, archive, and set each
 * stage's kind and win probability.
 *
 * Each change saves as it is made. The server keeps the rules (a pipeline
 * always has an open, a won and a lost stage; a stage with open deals cannot
 * be archived) and this screen shows its reason when it refuses.
 */
import { useEffect, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Group, Modal, NumberInput, Select, Stack, Switch, Table, Text, TextInput,
  Tooltip,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconPlus } from '@tabler/icons-react';
import { usePipelineEdits, usePipelines, type Pipeline, type PipelineStage, type StageKind } from '../../api/crm';
import { useVocab } from '../../vocab';

const KIND_OPTIONS: { value: StageKind; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

export function PipelinesModal({
  opened, onClose, initialId,
}: {
  opened: boolean;
  onClose: () => void;
  initialId?: string | null;
}) {
  const v = useVocab();
  const pipelines = usePipelines(opened).data?.pipelines ?? [];
  const edits = usePipelineEdits();
  const [selected, setSelected] = useState<string | null>(initialId ?? null);
  const [error, setError] = useState('');
  const [newPipeline, setNewPipeline] = useState('');
  const [newStage, setNewStage] = useState('');

  useEffect(() => {
    if (opened) {
      setSelected(initialId ?? null);
      setError('');
    }
  }, [opened, initialId]);

  const pipeline = pipelines.find((p) => p.id === selected) ?? pipelines[0];

  async function attempt(fn: () => Promise<unknown>) {
    setError('');
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function moveStage(p: Pipeline, index: number, by: -1 | 1) {
    const active = p.stages.filter((s) => !s.archived);
    const archived = p.stages.filter((s) => s.archived);
    const target = index + by;
    if (target < 0 || target >= active.length) return;
    const ids = active.map((s) => s.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    void attempt(() => edits.reorderStages.mutateAsync({
      id: p.id, stageIds: [...ids, ...archived.map((s) => s.id)],
    }));
  }

  return (
    <Modal opened={opened} onClose={onClose} title={v.Many('pipeline')} size="xl">
      <Stack>
        {error && <Alert color="red" variant="light" withCloseButton onClose={() => setError('')}>{error}</Alert>}
        <Group align="flex-end" wrap="wrap">
          <Select
            label={v.One('pipeline')}
            data={pipelines.map((p) => ({ value: p.id, label: p.archived ? `${p.name} (archived)` : p.name }))}
            value={pipeline?.id ?? null}
            onChange={setSelected}
            allowDeselect={false}
            miw={220}
          />
          <TextInput
            label={`New ${v.one('pipeline')}`}
            placeholder="Name"
            value={newPipeline}
            onChange={(e) => setNewPipeline(e.currentTarget.value)}
            miw={180}
          />
          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            disabled={!newPipeline.trim()}
            loading={edits.createPipeline.isPending}
            onClick={() => attempt(async () => {
              const p = await edits.createPipeline.mutateAsync({ name: newPipeline.trim() });
              setNewPipeline('');
              setSelected(p.id);
            })}
          >
            Add
          </Button>
        </Group>

        {pipeline && (
          <>
            <Group align="flex-end" wrap="wrap">
              <NameField
                key={`p-${pipeline.id}`}
                label="Name"
                value={pipeline.name}
                onSave={(name) => attempt(() => edits.updatePipeline.mutateAsync({ id: pipeline.id, body: { name } }))}
              />
              <Switch
                label="Archived"
                checked={pipeline.archived}
                onChange={(e) => {
                  const archived = e.currentTarget.checked;
                  void attempt(() => edits.updatePipeline.mutateAsync({ id: pipeline.id, body: { archived } }));
                }}
              />
            </Group>

            <Table.ScrollContainer minWidth={620}>
              <Table verticalSpacing={6} data-testid="stages-table">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={70}>Order</Table.Th>
                    <Table.Th>{v.One('stage')}</Table.Th>
                    <Table.Th w={120}>Kind</Table.Th>
                    <Table.Th w={130}>Win probability</Table.Th>
                    <Table.Th w={100}>Archived</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pipeline.stages.map((s) => {
                    const activeIndex = pipeline.stages.filter((x) => !x.archived).findIndex((x) => x.id === s.id);
                    return (
                      <StageRow
                        key={s.id}
                        stage={s}
                        canUp={!s.archived && activeIndex > 0}
                        canDown={!s.archived && activeIndex < pipeline.stages.filter((x) => !x.archived).length - 1}
                        onUp={() => moveStage(pipeline, activeIndex, -1)}
                        onDown={() => moveStage(pipeline, activeIndex, 1)}
                        onSave={(body) => attempt(() => edits.updateStage.mutateAsync({
                          id: pipeline.id, stageId: s.id, body,
                        }))}
                      />
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Group align="flex-end" wrap="wrap">
              <TextInput
                label={`New ${v.one('stage')}`}
                placeholder="Name"
                value={newStage}
                onChange={(e) => setNewStage(e.currentTarget.value)}
                miw={200}
              />
              <Button
                variant="default"
                leftSection={<IconPlus size={16} />}
                disabled={!newStage.trim()}
                loading={edits.createStage.isPending}
                onClick={() => attempt(async () => {
                  await edits.createStage.mutateAsync({ id: pipeline.id, body: { name: newStage.trim() } });
                  setNewStage('');
                })}
              >
                {`Add ${v.one('stage')}`}
              </Button>
            </Group>
            <Text size="xs" c="dimmed">
              {`A ${v.one('deal')} moved to a won stage is marked won, and to a lost stage marked lost, with a reason. Weighted value is the amount times the stage's win probability. A ${v.one('pipeline')} always keeps an open, a won and a lost stage.`}
            </Text>
          </>
        )}
      </Stack>
    </Modal>
  );
}

/** A text field that saves when it loses focus or Enter is pressed, if it changed. */
function NameField({ label, value, onSave }: { label: string; value: string; onSave: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => {
    const t = text.trim();
    if (t && t !== value) onSave(t);
    else setText(value);
  };
  return (
    <TextInput
      label={label}
      aria-label={label}
      value={text}
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      miw={200}
    />
  );
}

function StageRow({
  stage, canUp, canDown, onUp, onDown, onSave,
}: {
  stage: PipelineStage;
  canUp: boolean;
  canDown: boolean;
  onUp: () => void;
  onDown: () => void;
  onSave: (body: { name?: string; kind?: StageKind; win_probability?: number; archived?: boolean }) => void;
}) {
  const [name, setName] = useState(stage.name);
  const [prob, setProb] = useState<number | string>(stage.win_probability);
  useEffect(() => setName(stage.name), [stage.name]);
  useEffect(() => setProb(stage.win_probability), [stage.win_probability]);

  const commitName = () => {
    const t = name.trim();
    if (t && t !== stage.name) onSave({ name: t });
    else setName(stage.name);
  };
  const commitProb = () => {
    const n = typeof prob === 'number' ? prob : Number(prob);
    if (Number.isFinite(n) && n !== stage.win_probability) onSave({ win_probability: Math.round(n) });
    else setProb(stage.win_probability);
  };

  return (
    <Table.Tr style={{ opacity: stage.archived ? 0.6 : 1 }}>
      <Table.Td>
        <Group gap={2} wrap="nowrap">
          <Tooltip label="Move up" withArrow>
            <ActionIcon variant="subtle" size="sm" disabled={!canUp} onClick={onUp} aria-label={`Move ${stage.name} up`}>
              <IconArrowUp size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Move down" withArrow>
            <ActionIcon variant="subtle" size="sm" disabled={!canDown} onClick={onDown} aria-label={`Move ${stage.name} down`}>
              <IconArrowDown size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          aria-label={`Name of ${stage.name}`}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onBlur={commitName}
          onKeyDown={(e) => { if (e.key === 'Enter') commitName(); }}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          aria-label={`Kind of ${stage.name}`}
          data={KIND_OPTIONS}
          value={stage.kind}
          allowDeselect={false}
          onChange={(k) => k && k !== stage.kind && onSave({ kind: k as StageKind })}
        />
      </Table.Td>
      <Table.Td>
        <NumberInput
          size="xs"
          aria-label={`Win probability of ${stage.name}`}
          suffix="%"
          min={0}
          max={100}
          clampBehavior="strict"
          allowDecimal={false}
          value={prob}
          onChange={setProb}
          onBlur={commitProb}
        />
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Switch
            size="xs"
            aria-label={`Archive ${stage.name}`}
            checked={stage.archived}
            onChange={(e) => onSave({ archived: e.currentTarget.checked })}
          />
          {stage.archived && <Badge size="xs" variant="light" color="gray">Archived</Badge>}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
